import { useCallback, useEffect, useRef } from 'react';

import {
  NativeValidityStateKeys,
  ValidationError,
  type ValidationState,
} from './types.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';

/**
 * Returns a wrapped `emitValidationState` that always merges current custom
 * error messages into every emitted state. Also fires when the set of custom
 * errors changes (added or removed), using `getBaseState` to determine the
 * underlying native/Strato errors at that point.
 * @internal
 */
export function useCustomMessageEmitter(
  emitValidationState: ((state: ValidationState) => void) | undefined,
  getBaseState?: () => ValidationState | Promise<ValidationState>,
): (state: ValidationState) => ValidationState {
  const { state } = useFormFieldMessagesContext();

  // Stable primitive dep: detects add/remove/swap by ID and message text changes.
  const customErrorKey = state.customMessages
    .filter((m) => m.variant === 'error')
    .map((m) => `${m.id}:${state.customMessageTexts?.[m.id] ?? ''}`)
    .sort((a, b) => a.localeCompare(b))
    .join(',');

  // Read via ref so callbacks always see current values without re-creating.
  const customMessagesRef = useRef(state.customMessages);
  customMessagesRef.current = state.customMessages;

  const customMessageTextsRef = useRef(state.customMessageTexts);
  customMessageTextsRef.current = state.customMessageTexts;

  const controlIdsRef = useRef(state.controlIds);
  controlIdsRef.current = state.controlIds;

  const isTouchedRef = useRef(state.isTouched);
  isTouchedRef.current = state.isTouched;

  const getBaseStateRef = useRef(getBaseState);
  getBaseStateRef.current = getBaseState;

  // Wrapped emitter: merges current custom errors into every emitted state so
  // that onChange/onBlur calls never silently drop an active custom error.
  // Returns the merged state so callers (e.g. validateAsync) can return it
  // directly instead of the raw pre-merge state.
  const emitWithCustomErrors = useCallback(
    (validationState: ValidationState): ValidationState => {
      const customErrors = customMessagesRef.current.filter(
        (m) => m.variant === 'error',
      );

      // native/customError is set via setCustomValidity and is already
      // represented by our explicit custom entries — exclude to avoid doubling.
      const baseErrors = validationState.errors.filter(
        (e) => !e.validityStateKey.equals(NativeValidityStateKeys.customError),
      );

      // Prefer the form control ID from native errors (already knows the right
      // input element). Fall back to context-registered control IDs for cases
      // where there are no native errors.
      const baseControlId =
        baseErrors.find((e) => e.formControlId !== undefined)?.formControlId ??
        controlIdsRef.current;

      const merged: ValidationState = {
        ...validationState,
        errors: [
          ...baseErrors,
          ...customErrors.map(
            (m) =>
              new ValidationError(customMessageTextsRef.current?.[m.id] ?? '', {
                source: 'custom',
                formControlId: m.controlId ?? baseControlId,
              }),
          ),
        ],
      };

      emitValidationState?.(merged);
      return merged;
    },
    [emitValidationState],
  );

  const customErrorKeyRef = useRef(customErrorKey);
  customErrorKeyRef.current = customErrorKey;

  const scheduleEmit = useCallback(() => {
    let cancelled = false;
    const run = async () => {
      const baseState = (await getBaseStateRef.current?.()) ?? {
        status: 'done' as const,
        trigger: 'manual' as const,
        errors: [],
      };
      if (cancelled) {
        return;
      }
      emitWithCustomErrors(baseState);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [emitWithCustomErrors]);

  // Fires when the custom error set changes (add, remove, swap) so the consumer
  // always gets an up-to-date state without a value change being required.
  useEffect(() => {
    if (!emitValidationState || !isTouchedRef.current) {
      return;
    }
    return scheduleEmit();
  }, [customErrorKey, emitValidationState, scheduleEmit]);

  // Fires when isTouched flips to true while custom errors are already pending,
  // covering the case where errors were set before the field was ever blurred.
  useEffect(() => {
    if (
      !emitValidationState ||
      !isTouchedRef.current ||
      !customErrorKeyRef.current
    ) {
      return;
    }
    return scheduleEmit();
  }, [state.isTouched, emitValidationState, scheduleEmit]);

  return emitWithCustomErrors;
}
